import { marketingService } from '@/api';
import { Combobox, Options } from '@/components/ui/combobox';
import AppRoutes from '@/const/routes';
import clsx from 'clsx';
import { ChevronDown, Plus } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { useRouter } from 'next/router';
import { useNotification } from '@/hooks';
import { CasesWithCampaign } from '@/interfaces';
import { ErrorIcon } from '../error-handling';
import { useHandleError } from '@/hooks/useHandleError';
interface CampaignListProps {
	caseId: string;
	casesWithCampaignData?: CasesWithCampaign[];
	campaignIndex: number;
	refetch: () => void;
}

export const CampaignList: React.FC<CampaignListProps> = ({
	caseId,
	casesWithCampaignData,
	campaignIndex,
	refetch,
}) => {
	const router = useRouter();
	const [isOpen, setIsOpen] = React.useState(false);
	const [options, setOptions] = React.useState<Options[]>([]);
	const [value, setValue] = React.useState<string | undefined>();
	const { toastError } = useNotification();

	const { campaignHasError } = useHandleError();

	const hasError = campaignHasError((router.query['id'] as string) ?? '');

	React.useEffect(() => {
		if (casesWithCampaignData) {
			const aux: Options[] = [];

			casesWithCampaignData[0]?.campaigns.map((item, index) => {
				const hasError = campaignHasError(item.campaign_id);
				aux.push({
					value: `Campaign N° ${index + 1}`,
					label: (
						<div className="flex items-center gap-x-3 w-full ">
							<span className={clsx('block', { 'text-main-red': hasError })}>
								Campaign N° {index + 1}
							</span>
							{hasError && <ErrorIcon iconSize={20} />}
						</div>
					),
					onClick: () => {
						router.push({
							pathname:
								AppRoutes.LAWYERAPP_MARKETING_CAMPAIGN + `/${item.campaign_id}`,
						});
					},
				});
			});

			aux.push({
				value: '',
				label: (
					<span
						onClick={() => createNewCampaign()}
						className="flex items-center gap-x-2 cursor-pointer text-primary w-full"
					>
						<Plus className="size-6 text-primary" />
						<span>Add New Campaign</span>
					</span>
				),
			});
			setOptions(aux);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [casesWithCampaignData]);

	React.useEffect(() => {
		if (options.length > 0) {
			if (campaignIndex) {
				setValue(options[Number(campaignIndex) - 1]?.value);
			} else {
				setValue(options[0]?.value);
			}
		}
	}, [campaignIndex, options]);

	const createNewCampaign = async (): Promise<void> => {
		try {
			const res = await marketingService.postCreateCampaign({
				case_id: caseId,
			});
			refetch();
			router.push(`${AppRoutes.LAWYERAPP_MARKETING_CAMPAIGN}/${res.data.id}`);
		} catch (error) {
			toastError(
				'An error occurred while creating the campaign. Please try again.'
			);
		}
	};

	return (
		<div className="flex items-center gap-x-2.5">
			{hasError && <ErrorIcon iconSize={20} />}
			<Combobox
				open={isOpen}
				setOpen={setIsOpen}
				value={value}
				setValue={setValue}
				placeholder="View All"
				options={options}
				classNameTrigger="bg-white !w-[160px] mr-5"
				classNameButton="!justify-end"
				classNamePopoverContent="!w-[369px] max-h-[300px] overflow-y-auto scroll-custom"
				classNameCommandItem="!max-w-full"
				buttonContent={<ButtonCustom open={isOpen} label={value} />}
			/>
		</div>
	);
};

export const ButtonCustom: React.FC<{ open?: boolean; label?: string }> = ({
	label,
	open,
}) => {
	return (
		<Button
			variant="outline"
			aria-expanded={open}
			className={clsx('relative gap-2 min-w-[170px] bg-white !py-1 h-[35px]')}
		>
			<span className="text-primary font-semibold capitalize">{label}</span>
			<ChevronDown
				className={`mt-0.5 h-5 w-5 shrink-0 text-[#0D0616] ml-auto ${
					open && 'rotate-180'
				}`}
			/>
		</Button>
	);
};
